import React from 'react';
import AssignControl from './AssignControl';
import AiTakeoverControls from './AiTakeoverControls';
import { formatApptWhen, SMS_CONSENT_TYPES, consentView, formatPhone, shortDate, isHardStop } from './threadUtils';

/**
 * Context rail beside the thread: the patient, a handoff summary of the
 * conversation, next appointment, consent, open escalation, AI insights and
 * documents. Cards render only when they carry something (the patient and
 * summary cards always do). Two modes: 'open' (cards) and 'strip' (a 60px
 * icon strip with badges).
 */
function ContextRail({
  mode, pin, onPin, onModeChange,
  conversation, practiceId, patient, summary, summaryLoading, onRefreshSummary,
  appointment, consents, legacyConsent,
  escalation, insights, documents = [], documentCategories = [], busyDocumentId = null,
  onGrantConsent, onRevokeConsent, onAiStatusChange, onBookingLink,
  onRequestDocument, onSendDocuments, onOpenDocument, onRecategorize, onPushDocument,
}) {
  const cv = consentView(consents, legacyConsent);
  const hardStop = isHardStop(consents, legacyConsent);
  const escOpen = !!escalation;
  const hasIns = !!(insights?.sendTime?.best_hour_utc != null || insights?.sentiment?.sentiment_label);
  const openItems = (summary?.open || []).length;

  if (mode === 'strip') {
    return (
      <aside className="nh-rail" aria-label="Patient context (collapsed)">
        <div className="nh-strip">
          <button type="button" className="b patient" onClick={() => onModeChange('open')} title="Patient details">👤</button>
          <button type="button" className={`b summary${openItems ? '' : ' dim'}`} onClick={() => onModeChange('open')}
            title={openItems ? `${openItems} open item${openItems === 1 ? '' : 's'} in this conversation` : 'Conversation summary'}>
            📝{openItems ? <span className="n">{openItems}</span> : null}
          </button>
          <button type="button" className={`b appt${appointment ? '' : ' dim'}`} onClick={() => onModeChange('open')}
            title={appointment ? `Next visit: ${formatApptWhen(appointment)}${appointment.provider_name ? ' · ' + appointment.provider_name : ''}` : 'No upcoming visit'}>📅</button>
          <button type="button" className={`b consent${cv.anyOff ? '' : cv.known ? '' : ' dim'}`} onClick={() => onModeChange('open')}
            title={cv.anyOff ? `${cv.offCount} SMS channel${cv.offCount === 1 ? '' : 's'} off` : cv.known ? 'Consent: all SMS channels active' : 'Consent unknown'}>
            🛡{cv.anyOff ? <span className="n">{cv.offCount}</span> : null}
          </button>
          <button type="button" className={`b esc${escOpen ? '' : ' dim'}`} onClick={() => onModeChange('open')}
            title={escOpen ? 'Escalation open' : 'No open escalation'}>⚑{escOpen ? <span className="n">1</span> : null}</button>
          <button type="button" className={`b docs${documents.length ? '' : ' dim'}`} onClick={() => onModeChange('open')}
            title={documents.length ? `${documents.length} document${documents.length === 1 ? '' : 's'}` : 'No documents'}>
            📎{documents.length ? <span className="n">{documents.length}</span> : null}
          </button>
          {hasIns && (
            <button type="button" className="b ins" onClick={() => onModeChange('open')} title="AI insights">✦</button>
          )}
          <button type="button" className="open" onClick={() => onModeChange('open')}>Expand</button>
        </div>
      </aside>
    );
  }

  return (
    <aside className="nh-rail" aria-label="Patient context">
      <div className="rt">
        <span>Patient context</span>
        <div className="acts">
          <button type="button" className={pin === 'open' ? 'on' : ''} onClick={() => onPin(pin === 'open' ? 'auto' : 'open')}
            title={pin === 'open' ? 'Pinned open · click to let it collapse automatically' : 'Keep the rail open'}>
            {pin === 'open' ? '📌 Pinned' : 'Pin'}
          </button>
          <button type="button" onClick={() => onModeChange('strip')} title="Collapse to icons">Collapse ›</button>
        </div>
      </div>

      <PatientCard patient={patient} conversation={conversation} appointment={appointment} />

      <SummaryCard summary={summary} loading={summaryLoading} onRefresh={onRefreshSummary} />

      {appointment && (
        <div className="nh-card appt">
          <h4>📅 Next appointment</h4>
          <div className="body">
            <dl className="nh-kv">
              <dt>When</dt><dd>{formatApptWhen(appointment)}</dd>
              {appointment.provider_name && (<><dt>With</dt><dd>{appointment.provider_name}</dd></>)}
              {appointment.appointment_type && (<><dt>Type</dt><dd>{appointment.appointment_type}</dd></>)}
              <dt>Status</dt>
              <dd>{appointment.patient_confirmed_at ? 'Confirmed ✓' : 'Scheduled · unconfirmed'}</dd>
            </dl>
            {!appointment.patient_confirmed_at && onBookingLink && (
              <div className="act"><button type="button" className="btn" onClick={onBookingLink}>Insert booking link</button></div>
            )}
          </div>
        </div>
      )}

      {cv.known && !cv.anyOff && (
        <div className="nh-card consent summary">
          <h4>🛡 Consent</h4>
          <div className="body">
            <span>All SMS channels active</span>
            {cv.source && <small>{cv.source}</small>}
          </div>
        </div>
      )}
      {cv.known && cv.anyOff && (
        <div className="nh-card consent off">
          <h4>🛡 Consent</h4>
          <div className="body">
            {cv.rows.map(r => (
              <div key={r.type} className={`nh-cpill ${r.state}`}>
                <span>{r.label}</span>
                <span>{r.state === 'off' ? `Off${r.when ? ' · ' + r.when : ''}` : r.state === 'on' ? 'Active' : 'Unknown'}</span>
              </div>
            ))}
            {hardStop && (
              <small className="note">
                This patient texted STOP, so their carrier blocks our texts. Recording consent here
                cannot lift that — only the patient texting START to this number can. Call them instead.
              </small>
            )}
            {(onGrantConsent || onRevokeConsent) && conversation?.patient_id && (
              <div className="act">
                {cv.legacyGranted
                  ? onRevokeConsent && <button type="button" className="btn" onClick={() => onRevokeConsent(conversation.patient_id)}>Revoke SMS consent</button>
                  : onGrantConsent && !hardStop && <button type="button" className="btn grant" onClick={() => onGrantConsent(conversation.patient_id)}>Record verbal consent</button>}
              </div>
            )}
          </div>
        </div>
      )}
      {!cv.known && legacyConsent && conversation?.patient_id && (
        <div className="nh-card consent off">
          <h4>🛡 Consent</h4>
          <div className="body">
            <div className="nh-cpill unknown"><span>SMS consent</span><span>Not recorded</span></div>
            {onGrantConsent && (
              <div className="act"><button type="button" className="btn grant" onClick={() => onGrantConsent(conversation.patient_id)}>Record verbal consent</button></div>
            )}
          </div>
        </div>
      )}
      {conversation?.phi_notice_sent_at && (
        <div className="nh-card consent summary">
          <h4>🔒 Privacy notice</h4>
          <div className="body"><span>Sent {shortDate(conversation.phi_notice_sent_at)}</span><small>patient told texts are not fully secure</small></div>
        </div>
      )}

      {escOpen && (
        <div className="nh-card esc">
          <h4>⚑ Escalation · open</h4>
          <div className="body">
            <dl className="nh-kv">
              <dt>Reason</dt><dd>{escalation.reasonLabel}</dd>
              {escalation.raised && (<><dt>Raised</dt><dd>{escalation.raised}</dd></>)}
              <dt>Owner</dt><dd>{conversation?.assigned_to_user_id ? `User #${conversation.assigned_to_user_id}` : 'Unassigned'}</dd>
            </dl>
            <div className="act">
              <AssignControl conversation={conversation} practiceId={practiceId} onChanged={onAiStatusChange} className="btn" />
              <AiTakeoverControls practiceId={practiceId} subConversation={escalation.sub} onStatusChange={onAiStatusChange} />
            </div>
          </div>
        </div>
      )}

      {hasIns && (
        <div className="nh-card ins">
          <h4>✦ AI insights</h4>
          <div className="body">
            <dl className="nh-kv">
              {insights.sendTime?.best_hour_utc != null && (
                <><dt>Best time</dt><dd>{insights.sendTimeLabel}{insights.sendTime.response_rate != null ? ` · ${Math.round(insights.sendTime.response_rate * 100)}% reply rate` : ''}</dd></>
              )}
              {insights.sentiment?.sentiment_label && (
                <><dt>Sentiment</dt><dd>{cap(insights.sentiment.sentiment_label)}</dd></>
              )}
            </dl>
          </div>
        </div>
      )}

      {(documents.length > 0 || onRequestDocument || onSendDocuments) && (
        <div className="nh-card docs">
          <h4>📎 Documents</h4>
          <div className="body">
            {(onRequestDocument || onSendDocuments) && (
              <div className="doc-actions">
                {onRequestDocument && <button type="button" className="btn" onClick={onRequestDocument}>Request from patient</button>}
                {onSendDocuments && <button type="button" className="btn" onClick={onSendDocuments}>Send a file</button>}
              </div>
            )}
            {documents.map(d => (
              <DocumentCard key={d.id} doc={d} categories={documentCategories} busy={busyDocumentId === d.id}
                onOpen={onOpenDocument} onRecategorize={onRecategorize} onPush={onPushDocument} />
            ))}
            {documents.length === 0 && <small style={{ color: 'var(--t-ink3)' }}>Nothing exchanged yet.</small>}
          </div>
        </div>
      )}
    </aside>
  );
}

function PatientCard({ patient, conversation, appointment }) {
  const p = patient || {};
  const name = [p.first_name || conversation?.first_name, p.last_name || conversation?.last_name].filter(Boolean).join(' ');
  const dob = p.date_of_birth ? String(p.date_of_birth).slice(0, 10) : null;
  const age = dob ? ageFrom(dob) : null;
  const address = [p.address_line, p.city, [p.state, p.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  const rows = [
    ['Born', dob ? `${formatDob(dob)}${age != null ? ` · ${age}` : ''}` : null],
    ['Gender', p.gender || null],
    ['Mobile', p.mobile_phone ? formatPhone(p.mobile_phone) : (p.phone ? formatPhone(p.phone) : (conversation?.patient_phone ? formatPhone(conversation.patient_phone) : null))],
    ['Home', p.home_phone && p.home_phone !== p.mobile_phone ? formatPhone(p.home_phone) : null],
    ['Email', p.email || null],
    ['Address', address || null],
    ['Language', p.preferred_language && !/^en/i.test(p.preferred_language) ? p.preferred_language : null],
    ['Insurance', p.insurance_carrier || null],
    ['athena id', p.athena_patient_id || null],
    ['Last visit', p.last_visit_date ? shortDate(p.last_visit_date) : null],
    ['Emergency', p.emergency_contact_name ? `${p.emergency_contact_name}${p.emergency_contact_phone ? ' · ' + formatPhone(p.emergency_contact_phone) : ''}` : null],
  ].filter(([, v]) => v);
  // Care Hub's tabs are calendar / intake / consent — there is no patients tab. Deep-linking
  // ?appointment_id= forces the calendar tab and selects that appointment (commons #394).
  const careHub = appointment?.id
    ? `/practice-view/care-hub?tab=calendar&appointment_id=${appointment.id}`
    : (p.id ? '/practice-view/care-hub?tab=calendar' : null);
  return (
    <div className="nh-card patient">
      <h4>👤 Patient</h4>
      <div className="body">
        <div className="pname">{name || 'Patient'}</div>
        {rows.length > 0 && (
          <dl className="nh-kv">
            {rows.map(([k, v]) => (<React.Fragment key={k}><dt>{k}</dt><dd>{v}</dd></React.Fragment>))}
          </dl>
        )}
        {!patient && <small style={{ color: 'var(--t-ink3)' }}>Loading details…</small>}
        {careHub && (
          <div className="act">
            <a className="btn" href={careHub} title={appointment?.id ? 'Open this appointment in the Care Hub calendar' : 'Open the Care Hub calendar'}>
              {appointment?.id ? 'Open appointment in Care Hub' : 'Open in Care Hub'}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ summary, loading, onRefresh }) {
  const s = summary || {};
  const empty = !loading && (s.empty || (!s.discussed?.length && !s.done?.length && !s.open?.length && !s.next_step));
  return (
    <div className="nh-card summary-card">
      <h4>📝 Conversation so far
        {onRefresh && <button type="button" className="mini" onClick={onRefresh} disabled={loading} title="Rebuild from the latest messages">{loading ? '…' : '↻'}</button>}
      </h4>
      <div className="body">
        {loading && !summary && <small style={{ color: 'var(--t-ink3)' }}>Reading the thread…</small>}
        {s.error && <small style={{ color: '#9F1239' }}>{s.error}</small>}
        {empty && !s.error && <small style={{ color: 'var(--t-ink3)' }}>Nothing to summarise yet.</small>}
        {!empty && (
          <>
            {s.next_step && <div className="next"><b>Next</b><span>{s.next_step}</span></div>}
            <Section title="Open" items={s.open} cls="open" />
            <Section title="Discussed" items={s.discussed} />
            <Section title="Done" items={s.done} cls="done" />
            <small className="meta">{s.tone ? `Patient seems ${s.tone} · ` : ''}{s.message_count ? `${s.message_count} messages · ` : ''}{s.generated_at ? `updated ${new Date(s.generated_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : ''}</small>
          </>
        )}
      </div>
    </div>
  );
}

function Section({ title, items, cls = '' }) {
  if (!items || !items.length) return null;
  return (
    <div className={`sec ${cls}`}>
      <b>{title}</b>
      <ul>{items.map((it, i) => <li key={i}>{it}</li>)}</ul>
    </div>
  );
}

function DocumentCard({ doc, categories, busy, onOpen, onRecategorize, onPush }) {
  const inbound = doc.direction === 'inbound';
  const pushed = doc.status === 'pushed';
  const failed = doc.status === 'push_failed';
  const when = doc.created_at ? new Date(doc.created_at).toLocaleDateString([], { day: 'numeric', month: 'short' }) : '';
  const cat = categories.find(c => c.key === doc.category);
  const canPush = inbound && !pushed && !!cat?.pushable;
  return (
    <div className="nh-doc rich" id={`nh-doc-${doc.id}`}>
      <div className="th" role="button" tabIndex={0} title="Open" onClick={() => onOpen?.(doc)} onKeyDown={e => { if (e.key === 'Enter') onOpen?.(doc); }}
        style={doc.thumbnail_url ? { backgroundImage: `url(${doc.thumbnail_url})` } : { background: /pdf/i.test(doc.mime_type || '') ? '#E11D48' : 'linear-gradient(135deg, #C4B5FD, #F0ABFC)' }}>
        {!doc.thumbnail_url && (/pdf/i.test(doc.mime_type || '') ? 'PDF' : 'FILE')}
      </div>
      <div style={{ minWidth: 0 }}>
        {inbound && !pushed && categories.length > 0 && onRecategorize ? (
          <select value={doc.category} onChange={e => onRecategorize(doc, e.target.value)} disabled={busy} aria-label="Document type">
            {categories.map(c => <option key={c.key} value={c.key}>{c.label}{c.pushable ? '' : ' (view only)'}</option>)}
          </select>
        ) : (
          <div className="doc-name" title={doc.file_name || doc.label}>{inbound ? doc.label : doc.file_name || doc.label}</div>
        )}
        <small>{inbound ? `From patient · ${when}` : `Sent to patient · ${when}${doc.viewed_at ? ' · opened' : ' · not opened yet'}`}</small>
        <div className="doc-row">
          <button type="button" className="btn open" onClick={() => onOpen?.(doc)}>Open</button>
          {canPush && <button type="button" className="btn push" onClick={() => onPush?.(doc)} disabled={busy}>{busy ? 'Pushing…' : 'Push to athena'}</button>}
          {inbound && !pushed && cat && !cat.pushable && <span className="st out" title="athena has no document type for this through the Bridge yet">View only</span>}
          {pushed && <span className="st pushed">✓ In athena{doc.pushed_by_first_name ? ` · ${doc.pushed_by_first_name}` : ''}</span>}
          {failed && <span className="st failed" title={doc.push_error || ''}>✕ Push failed</span>}
        </div>
        {failed && doc.push_error && <small style={{ color: '#9F1239', whiteSpace: 'normal' }}>{doc.push_error}</small>}
      </div>
    </div>
  );
}

function cap(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }
function formatDob(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, (m || 1) - 1, d || 1);
  return isNaN(dt.getTime()) ? iso : dt.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
}
function ageFrom(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y) return null;
  const now = new Date();
  let age = now.getFullYear() - y;
  if (now.getMonth() + 1 < (m || 1) || (now.getMonth() + 1 === (m || 1) && now.getDate() < (d || 1))) age -= 1;
  return age >= 0 && age < 130 ? age : null;
}

export { SMS_CONSENT_TYPES };
export default ContextRail;
